import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCreateMailbox, useUpdateMailbox } from "@/hooks/use-mailbox"; // Assuming you have these hooks
import { MailboxType } from "@/types/mailbox.types";
import { Plus, X, Loader2, PlayCircle } from "lucide-react";
import * as React from "react";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Input, InputPassword } from "../ui/input";
import { toast } from "sonner";
import { useServices } from "@/services/hooks/useServices";
import { useState } from "react";
import { extractErrorMessage } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface MailboxWizardProps {
  mode?: "create" | "edit";
  initialData?: MailboxType;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  setShowAddMailboxDialog: (show: boolean) => void;
  showAddMailboxDialog: boolean;
  /** When "google_workspace_smtp", submit uses POST /mailbox/google-workspace-smtp instead of create mailbox */
  variant?: "imap" | "google_workspace_smtp";
}

const RESTRICTED_DOMAINS = [
  // Gmail
  "gmail.com",
  // Outlook
  "outlook.com",
  "hotmail.com",
  "live.com",
  // Yahoo
  "yahoo.com",
  "ymail.com",
  "rocketmail.com",
  // ProtonMail
  "proton.me",
  "protonmail.com",
  // Zoho
  "zoho.com",
  // GMX
  "gmx.com",
  "gmx.us",
  "gmx.net",
  // AOL
  "aol.com",
  // Mail.com
  "mail.com",
  "email.com",
  "usa.com",
  // Tutanota
  "tutanota.com",
  "tutanota.de",
  "tutamail.com",
  "tuta.io",
  "keemail.me",
  // Yandex
  "yandex.com",
  "yandex.ru",
  "yandex.ua",
  "yandex.kz",
  "yandex.by",
];

const isRestrictedDomain = (email: string) => {
  const domain = email.split("@")[1]?.toLowerCase();
  return RESTRICTED_DOMAINS.includes(domain);
};

export default function MailboxWizard({
  mode = "create",
  initialData,
  onSuccess,
  trigger,
  setShowAddMailboxDialog,
  showAddMailboxDialog,
  variant = "imap",
}: MailboxWizardProps) {
  const [step, setStep] = React.useState(1);
  const [formData, setFormData] = React.useState<Partial<MailboxType>>({
    firstName: "",
    lastName: "",
    email: "",
    smtpHost: "",
    smtpPort: 0,
    smtpPassword: "",
    smtpTls: false,
    imapHost: "",
    imapPort: 0,
    imapPassword: "",
    imapTls: false,
    ...initialData,
  });

  const queryClient = useQueryClient();
  const { mutate: createMailbox, isPending: isCreating } = useCreateMailbox();
  const { mutate: updateMailbox, isPending: isUpdating } = useUpdateMailbox();
  const { mailboxService } = useServices();
  const { mutate: addGoogleWorkspaceSmtp, isPending: isAddingGoogleWorkspaceSmtp } = useMutation({
    mutationFn: (data: Partial<MailboxType>) => mailboxService.addGoogleWorkspaceSmtp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
      queryClient.invalidateQueries({ queryKey: ["mailboxStats"] });
    },
  });
  const isLoading = isCreating || isUpdating || isAddingGoogleWorkspaceSmtp;
  const [isValidating, setIsValidating] = useState(false);

  const updateFormData = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!formData.firstName || !formData.lastName || !formData.email) {
        toast.error("Please fill in all personal details");
        return;
      }
      if (isRestrictedDomain(formData.email)) {
        toast.error(
          "Free email providers are not allowed. Please use a business email address.",
        );
        return;
      }
    }

    if (step === 2) {
      if (!formData.smtpHost || !formData.smtpPort || !formData.smtpPassword) {
        toast.error("Please fill in all SMTP details");
        return;
      }

      setIsValidating(true);
      try {
        const smtpResponse = await mailboxService.validateSmtp({
          auth: { user: formData.email!, pass: formData.smtpPassword! },
          host: formData.smtpHost!,
          port: formData.smtpPort!,
          secure: formData.smtpTls!,
        });

        if (smtpResponse.success) {
          toast.success("SMTP credentials verified successfully");
          setStep(step + 1);
        } else {
          toast.error("Invalid SMTP credentials. Please check and try again.");
        }
      } catch (error) {
        toast.error(
          "Failed to validate SMTP credentials. Please check your settings.",
        );
      } finally {
        setIsValidating(false);
      }
      return;
    }

    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const resetForm = () => {
    setStep(1);
    const defaultData = {
      firstName: "",
      lastName: "",
      email: "",
      smtpHost: "",
      smtpPort: 0,
      smtpPassword: "",
      smtpTls: false,
      imapHost: "",
      imapPort: 0,
      imapPassword: "",
      imapTls: false,
    };
    setFormData(initialData ? { ...defaultData, ...initialData } : defaultData);
  };
  const handleSubmit = async () => {
    try {
      if (step === 3) {
        setIsValidating(true);

        // Validate IMAP credentials
        const imapResponse = await mailboxService.validateImap({
          auth: { user: formData.email!, pass: formData.imapPassword! },
          host: formData.imapHost!,
          port: formData.imapPort!,
          secure: formData.imapTls!,
        });

        if (!imapResponse.success) {
          toast.error("Invalid IMAP credentials. Please check and try again.");
          return;
        }

        toast.success("IMAP credentials verified successfully");

        if (mode === "edit" && initialData?._id) {
          // Handle update
          updateMailbox(
            {
              id: initialData._id,
              mailboxData: {
                ...formData,
                // Ensure we don't send undefined values
                smtpPort: formData.smtpPort || 0,
                imapPort: formData.imapPort || 0,
                smtpTls: formData.smtpTls || false,
                imapTls: formData.imapTls || false,
              },
            },
            {
              onSuccess: () => {
                toast.success("Mailbox updated successfully");
                window.cioanalytics?.track("mailbox_updated", {
                  email: formData.email,
                });
                setShowAddMailboxDialog(false);
                onSuccess?.();
                resetForm();
              },
              onError: (error) => {
                console.error("Update error:", error);
                toast.error("Failed to update mailbox. Please try again.");
              },
            },
          );
        } else {
          // Handle create
          const payload = {
            ...formData,
            smtpPort: formData.smtpPort || 0,
            imapPort: formData.imapPort || 0,
            smtpTls: formData.smtpTls || false,
            imapTls: formData.imapTls || false,
          };
          if (variant === "google_workspace_smtp") {
            addGoogleWorkspaceSmtp(payload, {
              onSuccess: () => {
                toast.success("Mailbox created successfully");
                window.cioanalytics?.track("mailbox_added", { email: formData.email });
                setShowAddMailboxDialog(false);
                onSuccess?.();
                resetForm();
              },
              onError: (error) => {
                console.error("Create error:", error);
                toast.error(extractErrorMessage(error, "Failed to create mailbox. Please try again."));
              },
            });
          } else {
            createMailbox(payload, {
              onSuccess: () => {
                toast.success("Mailbox created successfully");
                window.cioanalytics?.track("mailbox_added", { email: formData.email });
                setShowAddMailboxDialog(false);
                onSuccess?.();
                resetForm();
              },
              onError: (error) => {
                console.error("Create error:", error);
                toast.error(extractErrorMessage(error, "Failed to create mailbox. Please try again."));
              },
            });
          }
        }
      } else {
        handleNext();
      }
    } catch (error) {
      console.error("Validation error:", error);
      toast.error(
        "Failed to verify IMAP credentials. Please check your settings.",
      );
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <>
      <Dialog
        open={showAddMailboxDialog}
        onOpenChange={(isOpen) => {
          setShowAddMailboxDialog(isOpen);
          if (!isOpen) resetForm();
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              {mode === "create" ? "Add New Mailbox" : "Edit Mailbox"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <div className="flex items-center">
              {[1, 2, 3].map((i) => (
                <React.Fragment key={i}>
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full ${
                      step >= i ? "bg-blue-600 text-white" : "bg-muted"
                    }`}
                  >
                    {i}
                  </div>
                  {i < 3 && (
                    <div
                      className={`h-1 w-10 ${
                        step > i ? "bg-blue-600" : "bg-muted"
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="pt-4">
            {step === 1 && (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 h-10 px-4 w-full sm:max-w-sm"
                    onClick={() =>
                      window.open("https://youtu.be/foSSqoX4rtI", "_blank")
                    }
                  >
                    <PlayCircle className="h-5 w-5" />
                    <span className="font-medium">See tutorial video</span>
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={(e) =>
                        updateFormData("firstName", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={(e) =>
                        updateFormData("lastName", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => {
                      const email = e.target.value;
                      updateFormData("email", email);
                      if (email && isRestrictedDomain(email)) {
                        e.target.setCustomValidity(
                          "Free email providers are not allowed",
                        );
                      } else {
                        e.target.setCustomValidity("");
                      }
                    }}
                    className={
                      formData.email && isRestrictedDomain(formData.email)
                        ? "border-red-500"
                        : ""
                    }
                  />
                  {formData.email && isRestrictedDomain(formData.email) && (
                    <p className="text-sm text-red-500">
                      Please use a business email address instead of a free
                      email provider
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtpHost">SMTP Host</Label>
                    <Input
                      id="smtpHost"
                      placeholder="SMTP Host"
                      value={formData.smtpHost}
                      onChange={(e) =>
                        updateFormData("smtpHost", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtpPort">SMTP Port</Label>
                    <Input
                      id="smtpPort"
                      type="number"
                      min={0}
                      placeholder="SMTP Port"
                      value={formData.smtpPort}
                      onChange={(e) =>
                        updateFormData(
                          "smtpPort",
                          e.target.value.replace(/[^0-9]/g, ""),
                        )
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtpPassword">SMTP Password</Label>
                  <InputPassword
                    id="smtpPassword"
                    placeholder="SMTP Password"
                    value={formData.smtpPassword}
                    onChange={(e) =>
                      updateFormData("smtpPassword", e.target.value)
                    }
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="smtpTls"
                    checked={formData.smtpTls}
                    onCheckedChange={(checked) =>
                      updateFormData("smtpTls", checked)
                    }
                  />
                  <Label htmlFor="smtpTls">SMTP TLS</Label>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="imapHost">IMAP Host</Label>
                    <Input
                      id="imapHost"
                      placeholder="IMAP Host"
                      value={formData.imapHost}
                      onChange={(e) =>
                        updateFormData("imapHost", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="imapPort">IMAP Port</Label>
                    <Input
                      id="imapPort"
                      type="number"
                      min={0}
                      placeholder="IMAP Port"
                      value={formData.imapPort}
                      onChange={(e) =>
                        updateFormData(
                          "imapPort",
                          e.target.value.replace(/[^0-9]/g, ""),
                        )
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="imapPassword">IMAP Password</Label>
                  <InputPassword
                    id="imapPassword"
                    placeholder="IMAP Password"
                    value={formData.imapPassword}
                    onChange={(e) =>
                      updateFormData("imapPassword", e.target.value)
                    }
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="imapTls"
                    checked={formData.imapTls}
                    onCheckedChange={(checked) =>
                      updateFormData("imapTls", checked)
                    }
                  />
                  <Label htmlFor="imapTls">IMAP TLS</Label>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 1}
            >
              Back
            </Button>
            {step < 3 ? (
              <Button onClick={handleNext} disabled={isValidating}>
                {isValidating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Validating...
                  </>
                ) : (
                  "Next"
                )}
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isLoading || isValidating}
              >
                {isLoading || isValidating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {mode === "edit" ? "Updating..." : "Adding..."}
                  </>
                ) : mode === "edit" ? (
                  "Update Mailbox"
                ) : (
                  "Add Mailbox"
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
